import PlatformModalLayer from '../utils/PlatformModalLayer';
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next';
import { Ripple } from '../utils/PopupRipple.jsx';
import './resetPasswordModal.scss';
import { toast } from "react-toastify";
import { useAccountManagerContext } from '../../contexts/Account-Manager-Context';
import APIMiddleware from '../../data/api/Api-Middleware';
import { useAuthContext } from '../../contexts/Auth-Context';
import { useChartContext } from '../../contexts/Chart-Context';
import Spinner from '../spinner/Spinner';
import { API_ENDPOINT_FORCE_PASSWORD_RESET } from '../../data/Endpoints-API';
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";

const ResetPasswordModal = () => {
  const { t } = useTranslation();
  const { setShowDeposit } = useAccountManagerContext();
  const { selectedStyle } = useChartContext();
  const { user, showResetPasswordModal, setIsFirstLoginDisabled } = useAuthContext();

  // form states
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // validation error states
  const [errors, setErrors] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // regex for password
  const passwordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const validateField = (field, value) => {
    let error = '';
    switch (field) {
      case 'newPassword':
        if (!value.trim()) error = t('auth.passwordRequired');
        else if (!passwordPattern.test(value)) {
          error = t('auth.passwordRules');
        }
        break;
      case 'confirmPassword':
        if (!value.trim()) error = t('auth.confirmRequired');
        else if (value !== newPassword) error = t('auth.passwordsMismatch');
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [field]: error }));
    return error;
  };

  const handleSubmit = async () => {
    // validate all fields
    const newPassErr = validateField('newPassword', newPassword);
    const confirmPassErr = validateField('confirmPassword', confirmPassword);

    if (newPassErr || confirmPassErr) {
      return; // do not proceed if validation fails
    }

    try {
      setLoading(true);

      let payload = { password: newPassword, password_confirmation: confirmPassword, user_detail_id: user?.userId };
      // let payload = { newPassword, confirmPassword };

      const response = await APIMiddleware.post(API_ENDPOINT_FORCE_PASSWORD_RESET(), payload);

      toast.success(response.message || t('auth.resetSuccess'), {
        position: "top-right",
      });
      showResetPasswordModal(false);
      setIsFirstLoginDisabled(false);
      window.location.reload();
    } catch (error) {
      if (error?.response?.data?.errors) {
        const errorObj = error.response.data.errors;

        // Loop through each field
        Object.keys(errorObj).forEach((field) => {
          const messages = errorObj[field];
          if (Array.isArray(messages)) {
            messages.forEach((msg) => {
              toast.error(msg, {
                position: "top-right",
              });
            });
          } else if (typeof messages === "string") {
            toast.error(messages, {
              position: "top-right",
            });
          }
        });
      } else {
        toast.error(t('auth.unknownError'), {
          position: "top-right",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PlatformModalLayer className="confirm-box-1" role="true">
      <div className="card">
        <h2>{t('auth.resetPasswordTitle')}</h2>

        {/* New Password */}
        <div className="mx-35 my-10">
          <div className="mt-5 mb-5">
            <label className="text-color">{t('auth.newPassword')}</label>
          </div>
          <div className="password-input-wrapper">
            <input
              className={`amountInput ${errors.newPassword ? 'input-error' : ''}`}
              type={showNewPassword ? "text" : "password"}
              onChange={(e) => {
                setNewPassword(e.target.value);
                validateField('newPassword', e.target.value);
              }}
              value={newPassword}
              placeholder={t('auth.enterNewPassword')}
            />
            <span
              className="eye-icon"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
            </span>
          </div>
          {errors.newPassword && <p className="error-text">{errors.newPassword}</p>}
        </div>

        {/* Confirm Password */}
        <div className="mx-35 my-10">
          <div className="mt-5 mb-5">
            <label className="text-color">{t('auth.confirmPassword')}</label>
          </div>
          <div className="password-input-wrapper">
            <input
              className={`amountInput ${errors.confirmPassword ? 'input-error' : ''}`}
              type={showConfirmPassword ? "text" : "password"}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                validateField('confirmPassword', e.target.value);
              }}
              value={confirmPassword}
              placeholder={t('auth.reEnterNewPassword')}
            />
            <span
              className="eye-icon"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
            </span>
          </div>
          {errors.confirmPassword && <p className="error-text">{errors.confirmPassword}</p>}
        </div>

        {/* Submit Button */}
        <div className="row-scss mt-15-custom">
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              position: "relative",
              overflow: "hidden",
              isolation: "isolate",
              backgroundColor: selectedStyle.buyColor,
            }}
          >
            <Ripple />
            {loading ? <Spinner /> : t('modals.submit')}
          </button>
        </div>
      </div>
    </PlatformModalLayer>
  );
};

export default ResetPasswordModal;
